import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Lottie from 'react-lottie';
import { useHistory, useParams } from 'react-router-dom';
import { Dialog } from '@material-ui/core';
import { offerMergingNotAllowedAffinityCodes } from '@ourbranch/lookups';

import branchSpinnerJson from 'core/assets/lottie/branch-spinner.min.json';
import infoDarkGreen from 'core/assets/svg/info-dark-green.svg';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { NotificationCard } from 'core/components/notification-card';
import { useQueryParams } from 'core/hooks/useQueryParams';
import { useStore } from 'core/store';
import offerUpdateIcon from './assets/offer-update.svg';
import useStyles from './rater-interstitial-modal.styles';

const itemsChangedMap = {
  coverageA: 'Replacement cost and dwelling limit',
  priorAutoCoverage: 'Prior auto coverage information',
  fpc: 'Fire protection class'
};

const Steps = {
  FETCH_MERGED_OFFER: 'FETCH_MERGED_OFFER',
  DONE: 'DONE'
};

const RaterInterstitialModal = ({ open }) => {
  const classes = useStyles();
  const history = useHistory();
  const { offerId } = useParams();
  const { rater } = useQueryParams();

  const {
    offer: { getMergedOffer, toggleRaterInterstitialModal, offer }
  } = useStore();

  const canMerge =
    offer &&
    !offerMergingNotAllowedAffinityCodes.includes(offer.quote.global.affinity) &&
    offer.quote.global.fromRC1 &&
    !offer.quote.fromStaff &&
    !offer.parentId;

  const [modalData, setModalData] = useState({
    state: Steps.FETCH_MERGED_OFFER,
    loading: true,
    offer: canMerge ? null : { id: offerId }
  });

  useEffect(() => {
    if (modalData.state === Steps.FETCH_MERGED_OFFER) {
      async function fetchMergedOffer() {
        const offer = await getMergedOffer(offerId, rater);
        if (offer) {
          toggleRaterInterstitialModal(false);
          history.push(`/offer/${offer.id}`);
          history.go(0);
        } else {
          toggleRaterInterstitialModal(false);
        }
      }
      fetchMergedOffer();
    } else {
      toggleRaterInterstitialModal(false);
    }
  }, [offerId, modalData.state, modalData.offer, modalData.loading]);

  if (modalData.loading) {
    return (
      <Dialog open={open} classes={{ paper: classes.loading }}>
        <div className={classes.spinner}>
          <Lottie
            options={{
              animationData: branchSpinnerJson,
              loop: true,
              autoplay: true
            }}
          />
        </div>
        <Label className={classes.spinnerText}>
          {modalData.state === Steps.FETCH_MERGED_OFFER
            ? 'Gathering offer details...'
            : 'Running preliminary reports...'}
        </Label>
      </Dialog>
    );
  }

  const hasCovAChanged = modalData.offer.itemsChanged.includes('coverageA');

  return (
    <Dialog open={open} classes={{ paper: classes.coveragesInfo }}>
      <img src={offerUpdateIcon} className={classes.icon} alt="Paper with two arrows forming a circle" />
      <Label type="h1">This offer's coverages and prices have been updated</Label>
      <div className={classes.content}>
        <Label className={classes.contentLabel} type="body1">
          We've run preliminary reports on this offer and have revised the following value(s) based on information from
          our trusted data sources:
        </Label>

        <ul className={classes.changes}>
          {modalData.offer.itemsChanged.map((change) => (
            <li key={change}>
              <Label className={classes.contentLabel} type="body1">
                {itemsChangedMap[change]}
              </Label>
            </li>
          ))}
        </ul>
      </div>
      {hasCovAChanged && (
        <NotificationCard
          className={classes.notification}
          type="light"
          icon={infoDarkGreen}
          iconSize="icon16"
          iconByHeader
        >
          <Label type="body2">
            In accordance with our underwriting guidelines, we allow adjusting the dwelling limit amount to range from
            100% to 120% of the estimated replacement cost, which is calculated based on current construction,
            materials, and labor costs.
          </Label>
        </NotificationCard>
      )}
      <Button
        className={classes.button}
        href={`/offer/${modalData.offer.id}`}
        variant="contained"
        color="primary"
        onClick={() => toggleRaterInterstitialModal(false)}
      >
        Continue to updated offer
      </Button>
    </Dialog>
  );
};

RaterInterstitialModal.propTypes = {
  open: PropTypes.bool.isRequired
};

export default observer(RaterInterstitialModal);
